import { useCallback, useEffect, useState } from 'react';
import { WhatsAppApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';

const TRIGGERS = [
  ['keyword', 'Keyword — when the message contains words'],
  ['welcome', 'Welcome — first message or after a long silence'],
  ['fallback', 'Fallback — nothing else matched'],
  ['step', 'Button reply — only sent when a button points to it'],
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Checkbox list used to pick one or more rules. */
function RulePicker({ rules, value, onChange, excludeId }) {
  const choices = rules.filter((r) => r.id !== excludeId);
  if (!choices.length) return <span className="muted">No other rules yet.</span>;
  return (
    <div className="wa-picker">
      {choices.map((r) => (
        <label key={r.id} className="check">
          <input
            type="checkbox"
            checked={value.includes(r.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, r.id] : value.filter((x) => x !== r.id))}
          />
          {r.name}
        </label>
      ))}
    </div>
  );
}

function RuleModal({ rule, rules, staff, onClose, onSaved }) {
  const editing = !!rule;
  const [form, setForm] = useState({
    name: rule?.name || '',
    trigger: rule?.trigger || 'keyword',
    match_type: rule?.match_type || 'contains',
    keywordsText: (rule?.keywords || []).join(', '),
    reply_text: rule?.reply_text || '',
    options: (rule?.options || []).map((o) => ({ title: o.title, rule_ids: o.rule_ids || [] })),
    next_rule_ids: rule?.next_rule_ids || [],
    action: rule?.action || 'none',
    assign_mode: rule?.assign_mode || 'none',
    assign_user_id: rule?.assign_user_id || '',
    priority: rule?.priority ?? 100,
    is_active: rule?.is_active ?? true,
  });
  const [media, setMedia] = useState(rule?.media || null);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setOption = (i, patch) =>
    setForm((f) => ({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    try {
      await WhatsAppApi.saveRule(rule?.id, {
        name: form.name,
        trigger: form.trigger,
        match_type: form.match_type,
        keywords: form.keywordsText.split(',').map((k) => k.trim()).filter(Boolean),
        reply_text: form.reply_text,
        options: form.options.filter((o) => o.title.trim()),
        next_rule_ids: form.next_rule_ids,
        action: form.action,
        assign_mode: form.assign_mode,
        assign_user_id: form.assign_mode === 'staff' ? Number(form.assign_user_id) || null : null,
        priority: Number(form.priority) || 100,
        is_active: form.is_active,
      });
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not save the rule.'));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    try {
      const updated = await WhatsAppApi.uploadRuleMedia(rule.id, file);
      setMedia(updated.media);
    } catch (err) {
      setBanner(errorMessage(err, 'Could not upload the file.'));
    }
  };

  const removeMedia = async () => {
    try {
      await WhatsAppApi.deleteRuleMedia(rule.id);
      setMedia(null);
    } catch (err) {
      setBanner(errorMessage(err, 'Could not remove the file.'));
    }
  };

  return (
    <Modal
      wide
      title={editing ? `Edit ${rule.name}` : 'New rule'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save rule'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Name *</label>
          <input type="text" value={form.name} onChange={set('name')} />
          {errors.name && <div className="err">{errors.name}</div>}
        </div>
        <div className="field">
          <label>When to send</label>
          <select value={form.trigger} onChange={set('trigger')}>
            {TRIGGERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {errors.trigger && <div className="err">{errors.trigger}</div>}
        </div>
        {form.trigger === 'keyword' && (
          <>
            <div className="field">
              <label>Keywords * (comma separated)</label>
              <input type="text" value={form.keywordsText} onChange={set('keywordsText')} placeholder="price, cost, rates" />
              {errors.keywords && <div className="err">{errors.keywords}</div>}
            </div>
            <div className="field">
              <label>Match</label>
              <select value={form.match_type} onChange={set('match_type')}>
                <option value="contains">Message contains the word / phrase</option>
                <option value="exact">Message is exactly the keyword</option>
                <option value="starts_with">Message starts with the keyword</option>
              </select>
            </div>
          </>
        )}
        <div className="field">
          <label>Reply * (use {'{name}'} and {'{phone}'})</label>
          <textarea rows={4} value={form.reply_text} onChange={set('reply_text')} />
          {errors.reply_text && <div className="err">{errors.reply_text}</div>}
        </div>

        <div className="field">
          <label>Buttons (up to 3 show as buttons, up to 10 as a list)</label>
          {form.options.map((o, i) => (
            <div key={i} className="wa-option">
              <div className="toolbar">
                <input
                  type="text"
                  maxLength={20}
                  placeholder="Button title"
                  value={o.title}
                  onChange={(e) => setOption(i, { title: e.target.value })}
                />
                <button type="button" className="icon-btn danger" onClick={() => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}>
                  <Icon name="trash" />
                </button>
              </div>
              <div className="sub">Sends:</div>
              <RulePicker rules={rules} excludeId={rule?.id} value={o.rule_ids} onChange={(ids) => setOption(i, { rule_ids: ids })} />
            </div>
          ))}
          {form.options.length < 10 && (
            <button type="button" className="btn btn-sm" onClick={() => setForm((f) => ({ ...f, options: [...f.options, { title: '', rule_ids: [] }] }))}>
              <Icon name="plus" size={14} /> Add button
            </button>
          )}
          {errors.options && <div className="err">{errors.options}</div>}
        </div>

        <div className="field">
          <label>Then also send</label>
          <RulePicker rules={rules} excludeId={rule?.id} value={form.next_rule_ids} onChange={(ids) => setForm((f) => ({ ...f, next_rule_ids: ids }))} />
          {errors.next_rule_ids && <div className="err">{errors.next_rule_ids}</div>}
        </div>

        <div className="field">
          <label>Image / video</label>
          {!editing && <span className="muted">Save the rule first, then edit it to attach media.</span>}
          {editing && media && (
            <div className="toolbar">
              <span className="badge badge-plain">{media.type}: {media.filename}</span>
              <button type="button" className="btn btn-sm" onClick={removeMedia}>Remove</button>
            </div>
          )}
          {editing && (
            <input type="file" accept="image/jpeg,image/png,video/mp4,video/3gpp" onChange={(e) => upload(e.target.files[0])} />
          )}
          {errors.file && <div className="err">{errors.file}</div>}
        </div>

        <div className="field">
          <label>After sending</label>
          <select value={form.action} onChange={set('action')}>
            <option value="none">Keep the bot active</option>
            <option value="handoff">Hand over to a person (pause the bot for this chat)</option>
          </select>
        </div>
        <div className="field">
          <label>Assign the chat to</label>
          <select value={form.assign_mode} onChange={set('assign_mode')}>
            <option value="none">Nobody</option>
            <option value="staff">A specific staff member</option>
          </select>
          {form.assign_mode === 'staff' && (
            <select style={{ marginTop: 8 }} value={form.assign_user_id} onChange={set('assign_user_id')}>
              <option value="">Select staff…</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {errors.assign_user_id && <div className="err">{errors.assign_user_id}</div>}
        </div>
        <div className="field">
          <label>Priority (lower runs first)</label>
          <input type="number" min="1" value={form.priority} onChange={set('priority')} />
        </div>
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function Simulator() {
  const [text, setText] = useState('');
  const [first, setFirst] = useState(true);
  const [out, setOut] = useState(null);
  const [error, setError] = useState('');

  const run = async (payload) => {
    setError('');
    try {
      setOut(await WhatsAppApi.simulate(payload));
    } catch (err) {
      setError(errorMessage(err, 'Simulation failed.'));
    }
  };

  const bubble = (r, key) => (
    <div className="wa-msg out" key={key}>
      <small className="muted">{r.rule_name ? `${r.rule_name} · ` : ''}{r.reason}</small>
      <p>{r.text}</p>
      {r.media && <div className="sub">+ {r.media.type}: {r.media.filename}</div>}
      <div className="toolbar">
        {r.options.map((o) => (
          <button key={o.id} type="button" className="btn btn-sm" onClick={() => run({ reply_id: o.id })}>{o.title}</button>
        ))}
      </div>
      {r.handoff && <div className="sub">→ hands over to a person</div>}
      {r.assign && <div className="sub">→ {r.assign}</div>}
    </div>
  );

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><strong>Test the bot</strong></div>
      <div style={{ padding: 20 }}>
        <div className="toolbar">
          <input className="select-sm" style={{ flex: 1 }} type="text" placeholder="Type a customer message…" value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run({ text, first_contact: first })} />
          <label className="check"><input type="checkbox" checked={first} onChange={(e) => setFirst(e.target.checked)} />First message</label>
          <button className="btn btn-primary" onClick={() => run({ text, first_contact: first })}>Test</button>
        </div>
        {error && <div className="notice notice-error">{error}</div>}
        {out && (
          <div className="wa-messages" style={{ minHeight: 0, marginTop: 12 }}>
            {out.reply ? [out.reply, ...out.more].map((r, i) => bubble(r, i)) : <em className="muted">The bot would not reply.</em>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BotPage() {
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [settingErrors, setSettingErrors] = useState({});
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit'|'delete', rule }

  const load = useCallback(async () => {
    try {
      const data = await WhatsAppApi.bot();
      setSettings(data.settings);
      setRules(data.rules);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the bot.'));
    }
  }, []);

  useEffect(() => {
    load();
    WhatsAppApi.botStaff().then(setStaff).catch(() => {});
  }, [load]);

  const patch = (p) => {
    setSaved(false);
    setSettings((s) => ({ ...s, ...p }));
  };

  const saveSettings = async () => {
    setBusy(true);
    setError('');
    setSettingErrors({});
    try {
      setSettings(await WhatsAppApi.saveBotSettings(settings));
      setSaved(true);
    } catch (err) {
      setSettingErrors(validationErrors(err));
      setError(errorMessage(err, 'Could not save settings.'));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    try {
      await WhatsAppApi.deleteRule(modal.rule.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete the rule.'));
    }
  };

  const toggleRule = async (r) => {
    try {
      await WhatsAppApi.saveRule(r.id, {
        name: r.name, trigger: r.trigger, match_type: r.match_type, keywords: r.keywords || [],
        reply_text: r.reply_text, options: r.options || [], next_rule_ids: r.next_rule_ids || [],
        action: r.action, assign_mode: r.assign_mode, assign_user_id: r.assign_user_id,
        priority: r.priority, is_active: !r.is_active,
      });
      load();
    } catch (err) {
      setError(errorMessage(err, 'Could not update the rule.'));
    }
  };

  if (!settings) return error ? <div className="notice notice-error">{error}</div> : <span className="skeleton" style={{ width: 240 }} />;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">WhatsApp bot</h1>
          <p className="page-sub">Automatic replies for incoming WhatsApp messages.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
          <Icon name="plus" size={16} />
          New rule
        </button>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><strong>Settings</strong></div>
        <div style={{ padding: 20 }}>
          <div className="field">
            <label className="check">
              <input type="checkbox" checked={settings.is_enabled} onChange={(e) => patch({ is_enabled: e.target.checked })} />
              Bot is on
            </label>
          </div>
          <div className="field">
            <label className="check">
              <input type="checkbox" checked={settings.pause_when_assigned} onChange={(e) => patch({ pause_when_assigned: e.target.checked })} />
              Stay silent once a staff member is assigned to the chat
            </label>
          </div>
          <div className="field">
            <label>Send the welcome message again after (hours of silence)</label>
            <input type="number" min="1" max="720" value={settings.welcome_after_hours} onChange={(e) => patch({ welcome_after_hours: Number(e.target.value) })} />
          </div>
          <div className="field">
            <label className="check">
              <input type="checkbox" checked={settings.hours_enabled} onChange={(e) => patch({ hours_enabled: e.target.checked })} />
              Only reply normally during business hours (away message otherwise)
            </label>
          </div>
          {settings.hours_enabled && (
            <>
              <div className="toolbar" style={{ marginBottom: 16 }}>
                <input className="select-sm" type="time" value={settings.hours_start} onChange={(e) => patch({ hours_start: e.target.value })} />
                <span>to</span>
                <input className="select-sm" type="time" value={settings.hours_end} onChange={(e) => patch({ hours_end: e.target.value })} />
                <input className="select-sm" type="text" value={settings.timezone} onChange={(e) => patch({ timezone: e.target.value })} placeholder="Asia/Kolkata" />
              </div>
              {settingErrors.timezone && <div className="err">{settingErrors.timezone}</div>}
              <div className="toolbar" style={{ marginBottom: 16 }}>
                {DAYS.map((d, i) => (
                  <label key={d} className="check">
                    <input
                      type="checkbox"
                      checked={(settings.hours_days || []).includes(i)}
                      onChange={(e) =>
                        patch({
                          hours_days: e.target.checked
                            ? [...(settings.hours_days || []), i]
                            : (settings.hours_days || []).filter((x) => x !== i),
                        })
                      }
                    />
                    {d}
                  </label>
                ))}
              </div>
              <div className="field">
                <label>Away message (use {'{name}'})</label>
                <textarea rows={3} value={settings.away_message || ''} onChange={(e) => patch({ away_message: e.target.value })} />
                {settingErrors.away_message && <div className="err">{settingErrors.away_message}</div>}
              </div>
            </>
          )}
          <button className="btn btn-primary" onClick={saveSettings} disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
          {saved && <span className="muted" style={{ marginLeft: 12 }}>Saved.</span>}
        </div>
      </div>

      <div className="card">
        <div className="card-head"><strong>Rules</strong><span className="muted">{rules.length} total</span></div>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Trigger</th>
                <th>Buttons</th>
                <th>Sent</th>
                <th>Active</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="name">{r.name}</div>
                    <div className="sub">{r.reply_text.slice(0, 70)}{r.reply_text.length > 70 ? '…' : ''}</div>
                  </td>
                  <td>
                    <span className="badge badge-plain">{r.trigger}</span>
                    {r.trigger === 'keyword' && <div className="sub">{(r.keywords || []).join(', ')}</div>}
                  </td>
                  <td>{(r.options || []).length || '—'}</td>
                  <td>{r.hit_count}</td>
                  <td>
                    <label className="check">
                      <input type="checkbox" checked={r.is_active} onChange={() => toggleRule(r)} />
                    </label>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="icon-btn" title="Edit" onClick={() => setModal({ mode: 'edit', rule: r })}><Icon name="edit" /></button>
                      <button className="icon-btn danger" title="Delete" onClick={() => setModal({ mode: 'delete', rule: r })}><Icon name="trash" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rules.length === 0 && (
          <div className="empty"><strong>No rules yet</strong>Add a welcome message and a few keyword replies.</div>
        )}
      </div>

      <Simulator />

      {(modal?.mode === 'create' || modal?.mode === 'edit') && (
        <RuleModal
          rule={modal.rule}
          rules={rules}
          staff={staff}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}
      {modal?.mode === 'delete' && (
        <ConfirmModal
          title="Delete rule"
          message={`Delete “${modal.rule.name}”? Buttons that point to it are removed.`}
          onConfirm={doDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
